<!-- MobileTemplateValidationModal.vue -->
<script setup>
import { ref, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MessagePreview from '../MessagePreview.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  template: {
    type: Object,
    default: null,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  recipientName: {
    type: String,
    default: 'João Silva',
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  parameterErrors: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'confirm', 'update-template']);

// Local state
const showTemplateVariables = ref(false);

// Template variables
const templateVariables = [
  {
    name: `{{nome}}`,
    description: 'Nome do contato (primeira parte)',
  },
  {
    name: `{{sobrenome}}`,
    description: 'Sobrenome do contato (segunda parte)',
  },
  { name: `{{nome_completo}}`, description: 'Nome completo do contato' },
  { name: `{{telefone}}`, description: 'Número de telefone do contato' },
];

// Methods
const toggleVariableDropdown = paramIndex => {
  showTemplateVariables.value =
    showTemplateVariables.value === paramIndex ? false : paramIndex;
};

const insertVariable = (variable, paramIndex) => {
  if (props.template?.parameters && props.template.parameters[paramIndex]) {
    const param = props.template.parameters[paramIndex];
    const currentValue = param.value || '';
    param.value = currentValue + variable;

    // Emit update event to parent
    emit('update-template', props.template);
  }
  // Hide variables dropdown
  showTemplateVariables.value = false;
};

const handleConfirm = () => {
  if (areParametersValid.value) {
    emit('confirm');
  }
};

// Template processing methods
const getTemplateContent = template => {
  if (!template.components) return '';

  let content = '';
  template.components.forEach(component => {
    if (component.type === 'HEADER' && component.text) {
      content += `**${component.text}**\n\n`;
    } else if (component.type === 'BODY' && component.text) {
      content += component.text + '\n\n';
    } else if (component.type === 'FOOTER' && component.text) {
      content += `_${component.text}_`;
    }
  });

  return content.trim();
};

const processVariablesInText = text => {
  if (!text) return text;

  let processed = text;

  // Replace template variables with sample values
  processed = processed.replace(/\{\{primeironome\}\}/g, 'João');
  processed = processed.replace(/\{\{sobrenome\}\}/g, 'Silva');
  processed = processed.replace(/\{\{nome_completo\}\}/g, 'João Silva');
  processed = processed.replace(/\{\{telefone\}\}/g, '+55 11 99999-9999');

  return processed;
};

const formatTemplatePreview = template => {
  let content = getTemplateContent(template);
  const parameters = template.parameters || [];

  parameters.forEach(param => {
    const placeholder = `{{${param.index}}}`;
    // Process template variables in the parameter value, then use it
    const processedValue = processVariablesInText(
      param.value || param.example || `{${param.index}}`
    );
    content = content.replace(new RegExp(placeholder, 'g'), processedValue);
  });

  return content;
};

// Computed properties
const areParametersValid = computed(() => {
  if (!props.template?.parameters || props.template.parameters.length === 0) {
    return true; // No parameters to validate
  }

  return props.template.parameters.every(
    param => param.value && param.value.trim() !== ''
  );
});

const missingParametersCount = computed(() => {
  if (!props.template?.parameters) return 0;

  return props.template.parameters.filter(
    param => !param.value || param.value.trim() === ''
  ).length;
});

const processedTemplatePreview = computed(() => {
  if (!props.template) return '';
  return formatTemplatePreview(props.template);
});
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-hidden shadow-xl"
    >
      <!-- Modal Header -->
      <div
        class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Confirmar Envio
        </h3>
        <button
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          @click="$emit('close')"
        >
          <i class="i-lucide-x text-xl" />
        </button>
      </div>

      <!-- Modal Content -->
      <div class="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
        <!-- Template Parameters -->
        <div
          v-if="
            template && template.parameters && template.parameters.length > 0
          "
          class="mb-6"
        >
          <div class="flex items-center gap-2 mb-4">
            <div
              class="w-8 h-8 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg flex items-center justify-center"
            >
              <i
                class="i-lucide-settings-2 text-sm text-blue-600 dark:text-blue-400"
              />
            </div>
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12">
                Parâmetros do Template
              </h4>
              <p class="text-xs text-n-slate-11">
                Personalize as variáveis do seu template
              </p>
            </div>
          </div>
          <div class="space-y-3">
            <div
              v-for="param in template.parameters"
              :key="param.index"
              class="group bg-gradient-to-r from-n-slate-1 to-n-slate-2 dark:from-n-slate-2 dark:to-n-slate-3 rounded-lg p-3 border border-n-slate-3 hover:border-n-brand/30 transition-all duration-200"
            >
              <div class="flex items-center gap-3">
                <!-- Parameter index badge -->
                <div
                  class="flex-shrink-0 w-7 h-7 bg-n-brand rounded-lg flex items-center justify-center"
                >
                  <span class="text-xs font-semibold text-white">{{
                    param.index
                  }}</span>
                </div>

                <!-- Input field -->
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <input
                      v-model="param.value"
                      type="text"
                      class="flex-1 px-3 py-2 text-sm bg-white dark:bg-n-slate-1 border rounded-lg placeholder:text-n-slate-10 text-n-slate-12 focus:outline-none focus:ring-2 transition-all duration-200 hover:border-n-slate-4 reset-base"
                      :class="[
                        parameterErrors[param.index]
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : 'border-n-slate-3 focus:ring-n-brand/20 focus:border-n-brand',
                      ]"
                      :placeholder="
                        param.example || `Valor para variável ${param.index}`
                      "
                    />

                    <!-- Variables button for each parameter -->
                    <div class="relative">
                      <button
                        class="w-8 h-8 bg-n-brand hover:bg-n-brand/80 rounded-lg flex items-center justify-center transition-colors duration-200"
                        title="Inserir variável"
                        @click="toggleVariableDropdown(param.index)"
                      >
                        <i class="i-lucide-braces text-white text-xs" />
                      </button>

                      <!-- Variables dropdown for this parameter -->
                      <div
                        v-if="showTemplateVariables === param.index"
                        class="absolute top-full right-0 mt-1 z-30 w-72 max-h-60 overflow-y-auto bg-n-background border border-n-slate-3 rounded-lg shadow-lg"
                      >
                        <div
                          class="p-2 text-xs text-n-slate-11 border-b border-n-slate-3"
                        >
                          Clique para inserir a variável no parâmetro
                          {{ param.index }}
                        </div>
                        <div class="divide-y divide-n-slate-3">
                          <div
                            v-for="variable in templateVariables"
                            :key="variable.name"
                            class="p-2 hover:bg-n-slate-1 cursor-pointer"
                            @click="
                              insertVariable(variable.name, param.index - 1)
                            "
                          >
                            <div class="flex flex-col">
                              <span
                                class="font-mono text-n-brand text-sm font-medium"
                                >{{ variable.name }}</span
                              >
                              <span class="text-xs text-n-slate-11 mt-1">{{
                                variable.description
                              }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Error message -->
                  <div v-if="parameterErrors[param.index]" class="mt-2 px-3">
                    <div
                      class="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-md border border-red-200 dark:border-red-800"
                    >
                      <i class="i-lucide-alert-circle text-xs" />
                      <span>{{ parameterErrors[param.index] }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- WhatsApp Preview -->
        <div class="border-t border-n-slate-3 dark:border-n-slate-7 pt-4">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
            Visualização WhatsApp
          </h4>
          <div class="bg-n-slate-1 dark:bg-n-slate-2 rounded-lg p-4">
            <MessagePreview
              v-if="template"
              type="whatsapp"
              :message="
                processedTemplatePreview || 'Sua mensagem aparecerá aqui'
              "
              :recipient-name="recipientName"
              :attachments="attachments"
              :compact="true"
            />
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="border-t border-gray-200 dark:border-gray-700">
        <!-- Warning message -->
        <div
          v-if="!areParametersValid && template?.parameters?.length > 0"
          class="px-4 pt-3 pb-2"
        >
          <div
            class="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-md border border-amber-200 dark:border-amber-800"
          >
            <i class="i-lucide-alert-triangle text-sm" />
            <span>
              <span v-if="missingParametersCount === 1"
                >Preencha o parâmetro restante para continuar</span
              >
              <span v-else
                >Preencha os {{ missingParametersCount }} parâmetros restantes
                para continuar</span
              >
            </span>
          </div>
        </div>

        <!-- Buttons -->
        <div class="flex gap-3 p-4">
          <Button
            label="Cancelar"
            variant="outline"
            color="slate"
            size="md"
            class="flex-1"
            @click="$emit('close')"
          />
          <Button
            :label="isSubmitting ? 'Enviando...' : 'Confirmar Envio'"
            :icon="isSubmitting ? 'i-lucide-loader' : 'i-lucide-send'"
            :variant="
              !areParametersValid && !isSubmitting ? 'outline' : 'solid'
            "
            :color="!areParametersValid && !isSubmitting ? 'slate' : 'blue'"
            size="md"
            class="flex-1"
            :disabled="isSubmitting || !areParametersValid"
            @click="handleConfirm"
          />
        </div>
      </div>
    </div>
  </div>
</template>
